import { useCallback } from "react";

import { DataFetcher } from "api/makeApiRequestType/makeFetchData";
import { useRemoteResource } from "hooks/useRemoteResource";
import { makeDummyRequest } from "api/makeApiRequest/makeDummyRequest";
import { SearchResults } from "types-and-validators/SearchResults";
import { MentorProduct } from "types-and-validators/MentorProduct";
import { addCurrencyConversions } from "api/helpers/addCurrencyConversions";

export interface ParamsBase {
	mentorId?: string;
	sessionTypeIds: string[];
	showDisabled?: boolean;
	doCurrencyConversion?: boolean;
	doWait?: boolean;
	repeatNumber?: number;
	persistBetweenLoads?: boolean;
	defaultCurrencyCode?: string;
	includePricesForGuestUid?: string;
}

// TODO:WV:20250312:Put this in a special ApiRequest maker
type ProductFetcherParams = {
	uid: string;
	sessionTypeId: string;

	showDisabled?: boolean;
	includePricesForGuestUid?: string;
};

export type Params<T extends MentorProduct> = ParamsBase & {
	requestType: string;
	fetchProducts: DataFetcher<ProductFetcherParams, SearchResults<T>>;
};

export type OutputRow<T extends MentorProduct> = T & {
	approximateAmountInDefaultCurrency?: number | undefined;
};

export function useMentorProducts<T extends MentorProduct>({
	mentorId,
	sessionTypeIds,
	showDisabled = false,
	doCurrencyConversion = true,
	doWait = false,
	repeatNumber = 1,
	persistBetweenLoads = false,
	defaultCurrencyCode,
	includePricesForGuestUid,
	requestType,
	fetchProducts
}: Params<T>) {
	// NB!  The following JSON.stringify is very important to prevent an infinite loop of re-running this callback repeatedly on the user profile page
	const sessionTypeIdsStr = JSON.stringify(sessionTypeIds);

	const cb = useCallback(
		(arg, auth) => {
			if (mentorId === undefined) {
				return makeDummyRequest(
					{ page: [] } as SearchResults<OutputRow<T>>,
					"dummy-" + requestType
				);
			}

			const ids: string[] = JSON.parse(sessionTypeIdsStr);
			const reqs = ids.map((sessionTypeId: string) =>
				fetchProducts(
					{
						uid: mentorId,
						sessionTypeId,
						showDisabled,
						includePricesForGuestUid
					},
					auth
				)
			);

			return {
				...addCurrencyConversions({
					reqs,
					doCurrencyConversion,
					defaultCurrencyCode,
					auth
				}),
				type: requestType
			};
		},
		[
			mentorId,
			defaultCurrencyCode,
			sessionTypeIdsStr,
			doCurrencyConversion,
			showDisabled,
			includePricesForGuestUid,
			fetchProducts,
			requestType
		]
	);

	return useRemoteResource<SearchResults<OutputRow<T>>>(
		cb,
		doWait,
		repeatNumber,
		persistBetweenLoads
	);
}
